import { useCallback, useEffect, useRef, useState } from "react";

/**
 * An on/off switch for an optional group of fields, where switching off does not destroy
 * what was typed.
 *
 * The public fair page renders a tool only when its fields are filled in, so "off" has to
 * mean "cleared" in the saved record. That makes the switch position un-derivable from the
 * draft once it is off, and naively clearing on toggle also throws the officer's work away —
 * flipping a tile off and back on used to blank it. Here the values are stashed on the way
 * out and put back on the way in, so the round trip is lossless; only an actual Save commits
 * the cleared state.
 *
 * `snapshot`/`clear`/`restore` are supplied by the caller because some tiles own flat draft
 * fields and others own a nested `link_visibility` entry.
 */
export const usePreservingToggle = <S,>({
  open,
  initiallyOn,
  snapshot,
  clear,
  restore,
}: {
  /** Dialog visibility — reopening re-seeds the switch from the saved record. */
  open: boolean;
  initiallyOn: boolean;
  snapshot: () => S;
  clear: () => void;
  restore: (stashed: S) => void;
}): [boolean, (next: boolean) => void] => {
  const [enabled, setEnabled] = useState(initiallyOn);
  const stash = useRef<S | null>(null);

  useEffect(() => {
    if (!open) return;
    setEnabled(initiallyOn);
    // Drop anything held from a previous visit; the saved record is the truth on open.
    stash.current = null;
    // Re-seeding is deliberately keyed on `open` alone. `initiallyOn` is derived from the
    // draft, which changes as the user types, and reacting to that would fight the switch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = useCallback(
    (next: boolean) => {
      setEnabled(next);
      if (next) {
        if (stash.current !== null) {
          restore(stash.current);
          stash.current = null;
        }
        return;
      }
      stash.current = snapshot();
      clear();
    },
    [snapshot, clear, restore]
  );

  return [enabled, toggle];
};
